import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const ASK_SIZES = ['$500', '$1K–$5K', '$5K–$25K', '$25K+'];

/**
 * Short interests quiz. Three free-form questions + optional ask-size chips.
 * All fields are optional but at least one must be provided. Raw answers are
 * saved immediately server-side and Gemma 4 extracts structured fields the
 * ideate step uses to find intersections with the user's prompt.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   initial: existing profile_json blob (or null) to pre-fill from
 */
export default function QuizDialog({ open, onClose, initial }) {
  const queryClient = useQueryClient();
  const initialRaw = initial?.raw || {};

  const [form, setForm] = useState({
    causes: initialRaw.causes || '',
    relationships: initialRaw.relationships || '',
    interests: initialRaw.interests || '',
    ask_comfort: initialRaw.ask_comfort || [],
  });

  const mutation = useMutation({
    mutationFn: (payload) => api.saveQuiz(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Don't auto-close — let the user see the "Saved" state and the extracted summary
    },
  });

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function toggleAskSize(size) {
    setForm((f) => {
      const has = f.ask_comfort.includes(size);
      return {
        ...f,
        ask_comfort: has
          ? f.ask_comfort.filter((s) => s !== size)
          : [...f.ask_comfort, size],
      };
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const hasAny =
      form.causes.trim() ||
      form.relationships.trim() ||
      form.interests.trim() ||
      form.ask_comfort.length > 0;
    if (!hasAny) return;
    mutation.mutate(form);
  }

  if (!open) return null;

  const structured = mutation.data?.profile_json?.structured;
  const warning = mutation.data?.warning;

  return (
    <div
      className="fixed inset-0 z-[2000] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-full overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 p-5 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Interests Quiz</h2>
            <p className="text-sm text-gray-600 mt-1 max-w-lg">
              Takes 60 seconds. The AI uses this to find <em>intersections</em> between your
              prospect searches and the people, causes, and interests you already have skin in.
              Every question is optional.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">
              What causes or communities do you personally care about?
            </label>
            <p className="text-xs text-gray-500 mb-2">
              e.g. "youth sports and mentoring", "food security", "public schools and literacy",
              "historic preservation in Arcadia"
            </p>
            <textarea
              value={form.causes}
              onChange={(e) => update('causes', e.target.value)}
              rows={2}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ccf-primary resize-none"
              placeholder="In your own words..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">
              Where do you already have strong personal or professional relationships?
            </label>
            <p className="text-xs text-gray-500 mb-2">
              The AI will flag prospects in these industries as <strong>warm intros</strong>. e.g.
              "commercial real estate owners", "restaurateurs — I know a lot of them", "AEC firms
              from my prior work", "other Biltmore parents"
            </p>
            <textarea
              value={form.relationships}
              onChange={(e) => update('relationships', e.target.value)}
              rows={2}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ccf-primary resize-none"
              placeholder="In your own words..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">
              What hobbies, interests, or conversation-starters make you <em>you</em>?
            </label>
            <p className="text-xs text-gray-500 mb-2">
              These become unexpected intersection angles. e.g. "I golf, cook, collect mid-century
              furniture, and travel a lot", "runner, wine, two kids in youth sports"
            </p>
            <textarea
              value={form.interests}
              onChange={(e) => update('interests', e.target.value)}
              rows={2}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ccf-primary resize-none"
              placeholder="In your own words..."
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-1">
              What ask sizes are you comfortable making? (optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Helps the AI calibrate which tier of prospect to send your way.
            </p>
            <div className="flex flex-wrap gap-2">
              {ASK_SIZES.map((size) => {
                const active = form.ask_comfort.includes(size);
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => toggleAskSize(size)}
                    className={`text-sm rounded-full px-3 py-1.5 border ${
                      active
                        ? 'bg-ccf-primary text-white border-ccf-primary'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Result preview */}
          {mutation.isSuccess && structured && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
              <div className="text-xs font-semibold text-emerald-900 uppercase mb-2">
                ✓ Saved — here's what the AI will use
              </div>
              {structured.summary_line && (
                <p className="text-sm text-emerald-900 italic mb-2">"{structured.summary_line}"</p>
              )}
              <ExtractedChips label="Causes" items={structured.causes} />
              <ExtractedChips label="Warm relationships" items={structured.industry_relationships} />
              <ExtractedChips label="Interests" items={structured.interests} />
              <ExtractedChips label="Neighborhood ties" items={structured.neighborhood_ties} />
              {structured.ask_comfort?.length > 0 && (
                <ExtractedChips label="Ask comfort" items={structured.ask_comfort} />
              )}
            </div>
          )}
          {mutation.isSuccess && warning && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
              {warning}
            </div>
          )}
          {mutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
              {mutation.error.message}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-500">
            Your answers are private to you and used only to personalize AI prospect searches.
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              {mutation.isSuccess ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={mutation.isPending}
              className="bg-ccf-primary text-white rounded px-4 py-2 text-sm hover:bg-blue-900 disabled:opacity-50"
            >
              {mutation.isPending
                ? 'Saving & extracting...'
                : mutation.isSuccess
                ? 'Update'
                : 'Save profile'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExtractedChips({ label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="text-xs text-emerald-800 font-medium">{label}</div>
      <div className="flex flex-wrap gap-1 mt-1">
        {items.map((item, i) => (
          <span
            key={i}
            className="text-xs bg-white border border-emerald-300 text-emerald-800 rounded px-2 py-0.5"
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}
